import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Users, CalendarDays, Clock, MapPin, ChevronLeft, ChevronRight,
  Check, Loader2, Sun, Moon as MoonIcon, Phone, Mail,
  User, PartyPopper, Briefcase, Heart, X, UtensilsCrossed,
  FileText, Star
} from 'lucide-react';
import { toast } from 'sonner';

const JOURS_HEADER = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const JOURS_FULL = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MOIS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const OCCASIONS = [
  { id: 'anniversaire', label: 'Anniversaire', icon: PartyPopper },
  { id: 'affaires', label: 'Affaires', icon: Briefcase },
  { id: 'romantique', label: 'Romantique', icon: Heart },
];

const ZONES = [
  { id: 'interieur', label: 'Interieur' },
  { id: 'veranda', label: 'Veranda' },
  { id: 'exterieur', label: 'Terrasse' },
];

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Form
  const [guests, setGuests] = useState(2);
  const [selectedDate, setSelectedDate] = useState('');
  const [zone, setZone] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [occasion, setOccasion] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Calendar
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());

  useEffect(() => {
    Promise.all([
      api.get('/resa/settings/public').then(r => setSettings(r.data)).catch(() => {}),
      api.get('/resa/menu').then(r => setMenuData(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    setTimeSlot('');
    api.get(`/resa/timeslots?date=${selectedDate}`)
      .then(r => setSlots(r.data || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const isDateDisabled = useCallback((dateStr) => {
    if (!settings) return true;
    const d = new Date(dateStr + 'T00:00:00');
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (d < todayStart) return true;
    const maxDate = new Date(todayStart);
    maxDate.setDate(maxDate.getDate() + 60);
    if (d > maxDate) return true;
    const dow = (d.getDay() + 6) % 7;
    if ((settings.closed_days || []).includes(dow)) return true;
    if ((settings.blocked_dates || []).includes(dateStr)) return true;
    return false;
  }, [settings, today]);

  const calendarDays = useMemo(() => {
    const first = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0).getDate();
    const startDay = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= lastDay; d++) {
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, date: dateStr, disabled: isDateDisabled(dateStr) });
    }
    return days;
  }, [calYear, calMonth, isDateDisabled]);

  const lunchSlots = slots.filter(s => s.service === 'lunch');
  const dinnerSlots = slots.filter(s => s.service === 'dinner');

  const navigateCalendar = (dir) => {
    let m = calMonth + dir, y = calYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setCalMonth(m);
    setCalYear(y);
  };

  const handleSubmit = async () => {
    if (!name || !email || !phone) { toast.error('Veuillez remplir tous les champs obligatoires'); return; }
    if (settings?.terms_text && !acceptTerms) { toast.error('Veuillez accepter les conditions'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/resa/reservations', {
        guest_name: name, guest_email: email, guest_phone: phone,
        date: selectedDate, time_slot: timeSlot, guests_count: guests,
        zone, special_requests: specialRequests, occasion,
      });
      setConfirmation(res.data);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la reservation');
    }
    setSubmitting(false);
  };

  const selectedDateObj = selectedDate ? new Date(selectedDate + 'T00:00:00') : null;
  const dateFr = selectedDateObj ? `${JOURS_FULL[selectedDateObj.getDay()]} ${selectedDateObj.getDate()} ${MOIS_FR[selectedDateObj.getMonth()]}` : '';
  const serviceLabel = timeSlot ? (timeSlot < '15:00' ? 'Dejeuner' : 'Diner') : '';

  if (loading) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#C19C7B]" />
    </div>
  );

  // ─── CONFIRMATION ───
  if (step === 4 && confirmation) {
    const cDate = new Date(confirmation.date + 'T00:00:00');
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" data-testid="booking-confirmation">
        <div className="w-full max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-[#C19C7B]/10 flex items-center justify-center mx-auto mb-6 animate-[scaleIn_0.4s_ease-out]">
            <Check size={36} className="text-[#C19C7B]" />
          </div>
          <h1 className="font-['Playfair_Display',serif] text-3xl text-[#2C2C2A] mb-2">Reservation confirmee</h1>
          <p className="text-sm text-[#8B8680] mb-8">Un email de confirmation a ete envoye a <strong className="text-[#2C2C2A]">{confirmation.guest_email}</strong></p>

          <div className="bg-white border border-[#EBE8E0] p-8 text-left space-y-4 mb-8">
            <div className="text-center mb-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#8B8680]">Numero de reservation</p>
              <p className="font-mono text-lg text-[#C19C7B] mt-1">{confirmation.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] mb-1">Date</p>
                <p className="text-sm text-[#2C2C2A] font-medium">{JOURS_FULL[cDate.getDay()]} {cDate.getDate()} {MOIS_FR[cDate.getMonth()]}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] mb-1">Heure</p>
                <p className="text-sm text-[#2C2C2A] font-mono">{confirmation.time_slot}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] mb-1">Convives</p>
                <p className="text-sm text-[#2C2C2A]">{confirmation.guests_count} {confirmation.guests_count > 1 ? 'personnes' : 'personne'}</p>
              </div>
              {confirmation.table_name && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] mb-1">Table</p>
                  <p className="text-sm text-[#2C2C2A]">{confirmation.table_name}</p>
                </div>
              )}
              {confirmation.zone && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] mb-1">Zone</p>
                  <p className="text-sm text-[#2C2C2A] capitalize">{confirmation.zone}</p>
                </div>
              )}
            </div>
          </div>

          <div className="text-center space-y-1">
            <p className="text-xs text-[#8B8680]">{settings?.restaurant_name || 'Le Mas'}</p>
            <p className="text-xs text-[#8B8680]">{settings?.phone}</p>
            <p className="text-xs text-[#8B8680]">{settings?.address}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="booking-page">
      {/* Header */}
      <header className="border-b border-[#EBE8E0] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-['Playfair_Display',serif] text-xl tracking-[0.15em] text-[#2C2C2A]">
              {settings?.restaurant_name || 'Le Mas'}
            </h1>
            {settings?.hero_subtitle && (
              <p className="text-[10px] tracking-[0.2em] text-[#8B8680] mt-0.5">{settings.hero_subtitle}</p>
            )}
          </div>
          <button onClick={() => setShowMenu(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#EBE8E0] text-xs text-[#2C2C2A] hover:bg-[#F5F3ED] transition-colors"
            data-testid="view-menu-btn">
            <UtensilsCrossed size={13} /> Voir notre carte
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress bar */}
        <div className="flex items-center justify-center gap-0 mb-10">
          {[
            { n: 1, label: 'Convives & Date' },
            { n: 2, label: 'Lieu & Heure' },
            { n: 3, label: 'Coordonnees' },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 flex items-center justify-center text-xs font-mono transition-all ${
                  step > s.n ? 'bg-[#C19C7B] text-white' : step === s.n ? 'bg-[#2C2C2A] text-white' : 'bg-[#EBE8E0] text-[#8B8680]'
                }`} data-testid={`step-indicator-${s.n}`}>
                  {step > s.n ? <Check size={14} /> : s.n}
                </div>
                <span className={`text-[9px] mt-1.5 tracking-wider uppercase ${step >= s.n ? 'text-[#2C2C2A]' : 'text-[#8B8680]'}`}>
                  {s.label}
                </span>
              </div>
              {i < 2 && <div className={`w-16 md:w-24 h-px mx-2 mt-[-14px] ${step > s.n ? 'bg-[#C19C7B]' : 'bg-[#EBE8E0]'}`} />}
            </div>
          ))}
        </div>

        <div className="flex gap-8">
          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* ═══ STEP 1 ═══ */}
            {step === 1 && (
              <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]" data-testid="step-1">
                {/* Guests */}
                <div>
                  <h2 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A] mb-4 flex items-center gap-2">
                    <Users size={18} className="text-[#C19C7B]" /> Nombre de convives
                  </h2>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                      <button key={n} onClick={() => setGuests(n)}
                        className={`py-4 text-center text-sm font-mono transition-all border ${
                          guests === n
                            ? 'bg-[#2C2C2A] text-white border-[#2C2C2A]'
                            : 'bg-white text-[#2C2C2A] border-[#EBE8E0] hover:border-[#C19C7B]'
                        }`} data-testid={`guests-${n}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Calendar */}
                <div>
                  <h2 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A] mb-4 flex items-center gap-2">
                    <CalendarDays size={18} className="text-[#C19C7B]" /> Choisir une date
                  </h2>
                  <div className="bg-white border border-[#EBE8E0] p-5">
                    <div className="flex items-center justify-between mb-5">
                      <button onClick={() => navigateCalendar(-1)} className="p-2 hover:bg-[#F5F3ED] text-[#8B8680] transition-colors" data-testid="cal-prev">
                        <ChevronLeft size={18} />
                      </button>
                      <span className="font-['Playfair_Display',serif] text-base text-[#2C2C2A]">
                        {MOIS_FR[calMonth]} {calYear}
                      </span>
                      <button onClick={() => navigateCalendar(1)} className="p-2 hover:bg-[#F5F3ED] text-[#8B8680] transition-colors" data-testid="cal-next">
                        <ChevronRight size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 mb-2">
                      {JOURS_HEADER.map((d, i) => (
                        <div key={i} className="text-center text-[10px] tracking-wider uppercase text-[#8B8680] py-2 font-medium">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-px">
                      {calendarDays.map((d, i) => d === null ? (
                        <div key={`e-${i}`} className="py-3" />
                      ) : (
                        <button key={d.date} onClick={() => { if (!d.disabled) setSelectedDate(d.date); }}
                          disabled={d.disabled}
                          className={`py-3 text-sm font-mono text-center transition-all ${
                            d.disabled
                              ? 'text-[#D5D0C8] cursor-not-allowed'
                              : selectedDate === d.date
                                ? 'bg-[#2C2C2A] text-white font-bold'
                                : d.date === todayStr
                                  ? 'bg-[#F5F3ED] text-[#C19C7B] font-bold hover:bg-[#EBE8E0]'
                                  : 'text-[#2C2C2A] hover:bg-[#F5F3ED]'
                          }`} data-testid={`date-${d.date}`}>
                          {d.day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <Button onClick={() => { if (selectedDate) setStep(2); else toast.error('Choisissez une date'); }}
                  className="w-full bg-[#2C2C2A] text-white hover:bg-[#3C3C3A] py-6 text-sm tracking-wider uppercase"
                  data-testid="next-step-1">
                  Continuer
                </Button>
              </div>
            )}

            {/* ═══ STEP 2 ═══ */}
            {step === 2 && (
              <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]" data-testid="step-2">
                <button onClick={() => { setStep(1); setTimeSlot(''); }} className="flex items-center gap-1 text-xs text-[#8B8680] hover:text-[#C19C7B] transition-colors">
                  <ChevronLeft size={14} /> Retour
                </button>

                {/* Zone */}
                {settings?.show_zone_selection && (
                  <div>
                    <h2 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A] mb-4 flex items-center gap-2">
                      <MapPin size={18} className="text-[#C19C7B]" /> Preference de lieu
                    </h2>
                    <div className="grid grid-cols-3 gap-2">
                      {ZONES.map(z => (
                        <button key={z.id} onClick={() => setZone(zone === z.id ? '' : z.id)}
                          className={`py-4 text-center text-sm border transition-all ${
                            zone === z.id
                              ? 'bg-[#2C2C2A] text-white border-[#2C2C2A]'
                              : 'bg-white text-[#2C2C2A] border-[#EBE8E0] hover:border-[#C19C7B]'
                          }`} data-testid={`zone-${z.id}`}>
                          {z.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Slots */}
                <div>
                  <h2 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A] mb-4 flex items-center gap-2">
                    <Clock size={18} className="text-[#C19C7B]" /> Choisir un creneau
                  </h2>

                  {slotsLoading ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C19C7B]" /></div>
                  ) : slots.length === 0 ? (
                    <div className="bg-white border border-[#EBE8E0] p-8 text-center">
                      <p className="text-sm text-[#8B8680]">Aucun creneau disponible pour cette date</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {lunchSlots.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Sun size={14} className="text-[#D4A843]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4A843] font-medium">Dejeuner</span>
                          </div>
                          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {lunchSlots.map(s => (
                              <button key={s.time} onClick={() => { if (s.available) setTimeSlot(s.time); }}
                                disabled={!s.available}
                                className={`py-3 text-center text-sm font-mono border transition-all ${
                                  !s.available
                                    ? 'text-[#D5D0C8] border-[#EBE8E0] bg-[#F8F6F2] cursor-not-allowed line-through'
                                    : timeSlot === s.time
                                      ? 'bg-[#2C2C2A] text-white border-[#2C2C2A]'
                                      : 'bg-white text-[#2C2C2A] border-[#EBE8E0] hover:border-[#C19C7B]'
                                }`} data-testid={`slot-${s.time}`}>
                                {s.time}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {dinnerSlots.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <MoonIcon size={14} className="text-[#7986CB]" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-[#7986CB] font-medium">Diner</span>
                          </div>
                          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {dinnerSlots.map(s => (
                              <button key={s.time} onClick={() => { if (s.available) setTimeSlot(s.time); }}
                                disabled={!s.available}
                                className={`py-3 text-center text-sm font-mono border transition-all ${
                                  !s.available
                                    ? 'text-[#D5D0C8] border-[#EBE8E0] bg-[#F8F6F2] cursor-not-allowed line-through'
                                    : timeSlot === s.time
                                      ? 'bg-[#2C2C2A] text-white border-[#2C2C2A]'
                                      : 'bg-white text-[#2C2C2A] border-[#EBE8E0] hover:border-[#C19C7B]'
                                }`} data-testid={`slot-${s.time}`}>
                                {s.time}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button onClick={() => { if (timeSlot) setStep(3); else toast.error('Choisissez un creneau'); }}
                  disabled={!timeSlot}
                  className="w-full bg-[#2C2C2A] text-white hover:bg-[#3C3C3A] py-6 text-sm tracking-wider uppercase disabled:opacity-40"
                  data-testid="next-step-2">
                  Continuer
                </Button>
              </div>
            )}

            {/* ═══ STEP 3 ═══ */}
            {step === 3 && (
              <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]" data-testid="step-3">
                <button onClick={() => setStep(2)} className="flex items-center gap-1 text-xs text-[#8B8680] hover:text-[#C19C7B] transition-colors">
                  <ChevronLeft size={14} /> Retour
                </button>

                <h2 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A] flex items-center gap-2">
                  <User size={18} className="text-[#C19C7B]" /> Vos coordonnees
                </h2>

                <div className="bg-white border border-[#EBE8E0] p-6 space-y-4">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] block mb-1.5">Nom complet *</label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C5C0B8]" />
                      <input type="text" value={name} onChange={e => setName(e.target.value)}
                        className="w-full h-11 pl-10 pr-4 border border-[#EBE8E0] bg-white text-sm text-[#2C2C2A] focus:outline-none focus:border-[#C19C7B] transition-colors"
                        placeholder="Jean Dupont" data-testid="booking-name" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] block mb-1.5">Email *</label>
                      <div className="relative">
                        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C5C0B8]" />
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                          className="w-full h-11 pl-10 pr-4 border border-[#EBE8E0] bg-white text-sm text-[#2C2C2A] focus:outline-none focus:border-[#C19C7B] transition-colors"
                          placeholder="jean@email.com" data-testid="booking-email" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] block mb-1.5">Telephone *</label>
                      <div className="relative">
                        <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C5C0B8]" />
                        <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                          className="w-full h-11 pl-10 pr-4 border border-[#EBE8E0] bg-white text-sm text-[#2C2C2A] focus:outline-none focus:border-[#C19C7B] transition-colors"
                          placeholder="+33 6 12 34 56 78" data-testid="booking-phone" />
                      </div>
                    </div>
                  </div>

                  {/* Occasion */}
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] block mb-2">Occasion</label>
                    <div className="flex gap-2">
                      {OCCASIONS.map(o => {
                        const Icon = o.icon;
                        return (
                          <button key={o.id} onClick={() => setOccasion(occasion === o.id ? '' : o.id)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 border text-xs transition-all ${
                              occasion === o.id
                                ? 'bg-[#2C2C2A] text-white border-[#2C2C2A]'
                                : 'bg-white text-[#2C2C2A] border-[#EBE8E0] hover:border-[#C19C7B]'
                            }`} data-testid={`occasion-${o.id}`}>
                            <Icon size={13} /> {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Special requests */}
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B8680] block mb-1.5">Demandes speciales</label>
                    <textarea value={specialRequests} onChange={e => setSpecialRequests(e.target.value)}
                      className="w-full h-24 px-4 py-3 border border-[#EBE8E0] bg-white text-sm text-[#2C2C2A] resize-none focus:outline-none focus:border-[#C19C7B] transition-colors"
                      placeholder="Allergies, preferences, chaise bebe, handicap..."
                      data-testid="booking-requests" />
                  </div>

                  {/* Terms */}
                  {settings?.terms_text && (
                    <div className="border-t border-[#EBE8E0] pt-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                          className="mt-0.5" data-testid="accept-terms" />
                        <span className="text-xs text-[#8B8680]">
                          {settings.terms_text}{' '}
                          <button type="button" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                            className="text-[#C19C7B] underline" data-testid="show-terms">
                            Lire les conditions
                          </button>
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                <Button onClick={handleSubmit} disabled={submitting || !name || !email || !phone}
                  className="w-full bg-[#C19C7B] text-white hover:bg-[#A8836A] py-6 text-sm tracking-wider uppercase gap-2 disabled:opacity-40"
                  data-testid="submit-booking">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Confirmer la reservation
                </Button>
              </div>
            )}
          </div>

          {/* ═══ SIDEBAR RECAP ═══ */}
          <div className="hidden lg:block w-72 shrink-0">
            <div className="bg-white border border-[#EBE8E0] p-5 sticky top-24" data-testid="booking-sidebar">
              <h3 className="font-['Playfair_Display',serif] text-sm text-[#2C2C2A] mb-4">Votre reservation</h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-[#EBE8E0]">
                  <Users size={14} className="text-[#C19C7B] shrink-0" />
                  <div>
                    <p className="text-[9px] tracking-wider uppercase text-[#8B8680]">Convives</p>
                    <p className="text-sm text-[#2C2C2A] font-medium">{guests} {guests > 1 ? 'personnes' : 'personne'}</p>
                  </div>
                </div>

                {selectedDate && (
                  <div className="flex items-center gap-3 pb-3 border-b border-[#EBE8E0]">
                    <CalendarDays size={14} className="text-[#C19C7B] shrink-0" />
                    <div>
                      <p className="text-[9px] tracking-wider uppercase text-[#8B8680]">Date</p>
                      <p className="text-sm text-[#2C2C2A] font-medium">{dateFr}</p>
                    </div>
                  </div>
                )}

                {timeSlot && (
                  <div className="flex items-center gap-3 pb-3 border-b border-[#EBE8E0]">
                    <Clock size={14} className="text-[#C19C7B] shrink-0" />
                    <div>
                      <p className="text-[9px] tracking-wider uppercase text-[#8B8680]">{serviceLabel}</p>
                      <p className="text-sm text-[#2C2C2A] font-mono font-medium">{timeSlot}</p>
                    </div>
                  </div>
                )}

                {zone && (
                  <div className="flex items-center gap-3 pb-3 border-b border-[#EBE8E0]">
                    <MapPin size={14} className="text-[#C19C7B] shrink-0" />
                    <div>
                      <p className="text-[9px] tracking-wider uppercase text-[#8B8680]">Zone</p>
                      <p className="text-sm text-[#2C2C2A] capitalize">{zone}</p>
                    </div>
                  </div>
                )}

                {name && (
                  <div className="flex items-center gap-3">
                    <User size={14} className="text-[#C19C7B] shrink-0" />
                    <div>
                      <p className="text-[9px] tracking-wider uppercase text-[#8B8680]">Nom</p>
                      <p className="text-sm text-[#2C2C2A] font-medium">{name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#EBE8E0] py-6 mt-12">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between text-[10px] text-[#8B8680]">
          <span>{settings?.restaurant_name} &middot; {settings?.phone}</span>
          <span>{settings?.address}</span>
        </div>
      </footer>

      {/* ═══ MENU MODAL ═══ */}
      {showMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowMenu(false)}>
          <div className="bg-white w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="menu-modal">
            <div className="sticky top-0 bg-white border-b border-[#EBE8E0] px-6 py-4 flex items-center justify-between">
              <h3 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A]">Notre Carte</h3>
              <button onClick={() => setShowMenu(false)} className="text-[#8B8680] hover:text-[#2C2C2A]"><X size={18} /></button>
            </div>
            <div className="px-6 py-4">
              {menuData.length === 0 ? (
                <p className="text-sm text-[#8B8680] text-center py-8">La carte sera bientot disponible</p>
              ) : (
                menuData.map(cat => (
                  <div key={cat.id} className="mb-6">
                    <h4 className="font-['Playfair_Display',serif] text-base text-[#2C2C2A] mb-1">{cat.name}</h4>
                    {cat.description && <p className="text-xs text-[#8B8680] mb-3">{cat.description}</p>}
                    <div className="space-y-2">
                      {(cat.items || []).map(item => (
                        <div key={item.id} className="flex justify-between items-start py-2 border-b border-[#EBE8E0]/50 last:border-0">
                          <div className="flex-1 pr-4">
                            <p className="text-sm text-[#2C2C2A]">{item.name}</p>
                            {item.description && <p className="text-[10px] text-[#8B8680] mt-0.5">{item.description}</p>}
                            {item.allergens && <p className="text-[9px] text-[#C19C7B] mt-0.5">Allergenes: {item.allergens}</p>}
                          </div>
                          <span className="text-sm font-mono text-[#2C2C2A] shrink-0">{item.price?.toFixed(2)} &euro;</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ CGV MODAL ═══ */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowTerms(false)}>
          <div className="bg-white w-full max-w-md mx-4 max-h-[70vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="terms-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Playfair_Display',serif] text-lg text-[#2C2C2A]">Conditions generales</h3>
              <button onClick={() => setShowTerms(false)} className="text-[#8B8680] hover:text-[#2C2C2A]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#5A5650] whitespace-pre-wrap">{settings?.terms_text || 'Aucune condition configuree.'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;
